import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { HelpCircle, MessageSquare, Clock, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'account', label: 'Account Issues' },
  { value: 'tournament', label: 'Tournament Help' },
  { value: 'payment', label: 'Payment Problems' },
  { value: 'technical', label: 'Technical Support' },
  { value: 'other', label: 'Other' }
];

export function Support() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    subject: '',
    message: '',
    category: ''
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const { data } = await axios.get(`${API}/support/tickets`, { withCredentials: true });
      setTickets(data.tickets || []);
    } catch (e) {
      console.error('Failed to fetch tickets:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message || !form.category) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/ticket`, form, { withCredentials: true });
      setForm({ subject: '', message: '', category: '' });
      setDialogOpen(false);
      fetchTickets();
    } catch (e) {
      console.error('Failed to create ticket:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <Clock className="w-4 h-4 text-[#00F0FF]" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      default:
        return <Clock className="w-4 h-4 text-[#A1A1AA]" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open':
        return 'bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30';
      case 'resolved':
        return 'bg-green-400/10 text-green-400 border-green-400/30';
      case 'pending':
        return 'bg-yellow-400/10 text-yellow-400 border-yellow-400/30';
      default:
        return 'bg-[#27272A] text-[#A1A1AA]';
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="support-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-white mb-2">
              Support Center
            </h1>
            <p className="text-[#A1A1AA]">
              Get help with your account, tournaments, or technical issues
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-bold" data-testid="create-ticket-btn">
                <MessageSquare className="w-4 h-4 mr-2" />
                New Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121214] border-[#27272A] text-white">
              <DialogHeader>
                <DialogTitle className="font-outfit text-xl">Create Support Ticket</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="bg-[#1A1A1E] border-[#27272A] text-white" data-testid="ticket-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-[#27272A]">
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Subject</Label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                    className="bg-[#1A1A1E] border-[#27272A] text-white placeholder:text-[#A1A1AA]"
                    data-testid="ticket-subject-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Message</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Describe your issue in detail..."
                    rows={4}
                    className="bg-[#1A1A1E] border-[#27272A] text-white placeholder:text-[#A1A1AA] resize-none"
                    data-testid="ticket-message-input"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1 border-[#27272A] text-white hover:bg-[#1A1A1E]"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-bold"
                    disabled={submitting || !form.subject || !form.message || !form.category}
                    data-testid="submit-ticket-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit Ticket'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Quick Help */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in stagger-1">
          <div className="p-4 rounded-md bg-[#121214] border border-[#27272A] card-hover">
            <HelpCircle className="w-8 h-8 text-[#00F0FF] mb-3" />
            <h3 className="font-outfit font-bold text-white mb-1">FAQ</h3>
            <p className="text-sm text-[#A1A1AA]">Find answers to common questions</p>
          </div>
          <div className="p-4 rounded-md bg-[#121214] border border-[#27272A] card-hover">
            <MessageSquare className="w-8 h-8 text-[#7000FF] mb-3" />
            <h3 className="font-outfit font-bold text-white mb-1">AI Chat</h3>
            <p className="text-sm text-[#A1A1AA]">Get instant help from our AI</p>
          </div>
          <div className="p-4 rounded-md bg-[#121214] border border-[#27272A] card-hover">
            <Send className="w-8 h-8 text-[#FF0055] mb-3" />
            <h3 className="font-outfit font-bold text-white mb-1">Contact Us</h3>
            <p className="text-sm text-[#A1A1AA]">Reach out directly to our team</p>
          </div>
        </div>

        {/* Tickets List */}
        <div className="animate-fade-in stagger-2">
          <h2 className="font-outfit font-bold text-xl text-white mb-4">Your Tickets</h2>
          
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, idx) => (
                <div key={idx} className="h-20 rounded-md bg-[#121214] animate-pulse" />
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-16 rounded-md bg-[#121214] border border-[#27272A]">
              <HelpCircle className="w-16 h-16 mx-auto text-[#27272A] mb-4" />
              <h3 className="font-outfit font-bold text-xl text-white mb-2">No tickets yet</h3>
              <p className="text-[#A1A1AA] mb-4">Create a ticket when you need help</p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-bold"
              >
                Create Your First Ticket
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket, idx) => (
                <div 
                  key={ticket.id}
                  className="p-4 rounded-md bg-[#121214] border border-[#27272A] card-hover animate-fade-in"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                  data-testid={`ticket-${ticket.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${getStatusClass(ticket.status)}`}>
                          {getStatusIcon(ticket.status)}
                          {ticket.status}
                        </span>
                        <span className="text-xs text-[#A1A1AA] px-2 py-0.5 rounded-full bg-[#1A1A1E]">
                          {categories.find(c => c.value === ticket.category)?.label || ticket.category}
                        </span>
                      </div>
                      <h3 className="font-semibold text-white truncate">{ticket.subject}</h3>
                      <p className="text-sm text-[#A1A1AA] mt-1 line-clamp-2">{ticket.message}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-[#A1A1AA]">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
